'use client';

import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { motion } from 'motion/react';

export default function RealTimeClock() {
  const [time, setTime] = useState<Date | null>(null);

  useEffect(() => {
    const updateTime = () => setTime(new Date());
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  if (!time) {
    return <div className="animate-pulse h-8 w-40 bg-slate-200 rounded-md"></div>;
  }

  const formattedDate = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(time).toLowerCase();

  const formattedTime = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(time);

  return (
    <div className="flex items-center bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-lg overflow-hidden group hover:bg-white/20 transition-all duration-500">
      {/* Time Section */}
      <div className="px-4 py-2 flex items-center gap-3 border-r border-white/10">
        <div className="relative flex items-center justify-center">
          <Clock className="w-5 h-5 text-blue-200 group-hover:text-white transition-colors" />
          <motion.div 
            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute w-full h-full bg-blue-400 rounded-full"
          />
        </div>
        <div className="flex items-baseline gap-0.5">
          <span className="font-black text-xl md:text-2xl tracking-tighter text-white tabular-nums">
            {formattedTime.split(':')[0]}:{formattedTime.split(':')[1]}
          </span>
          <span className="text-[10px] font-bold text-blue-300 tabular-nums opacity-80 mb-1">
            {formattedTime.split(':')[2]}
          </span>
        </div>
      </div>
      
      {/* Date Section */}
      <div className="px-4 py-2 hidden sm:flex flex-col justify-center bg-black/5">
        <span className="text-[10px] uppercase tracking-[0.2em] font-black text-blue-100/90 leading-none mb-1">
          {formattedDate.split(',')[0]}
        </span>
        <span className="text-[9px] font-bold text-blue-200/60 leading-none">
          {formattedDate.includes(',') ? formattedDate.split(',')[1] : ''}
        </span>
      </div>
      
      {/* Mobile Date (Compact) */}
      <div className="px-3 py-2 sm:hidden flex items-center">
        <span className="text-[10px] font-black text-blue-100/80 uppercase tracking-tighter">
          {time.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
        </span>
      </div>
    </div>
  );
}
